import { useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Paper, Typography, Stack, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams, GridRowParams } from "@mui/x-data-grid";
import { useLazyFetchOrganizationOrdersQuery, useGetOrderByIdQuery } from "@/features/organizations/api/ordersApi";
import { format } from "date-fns";
import { Order } from "@/types";
import { sanitizeForSearch } from "@/utils/names";
import OrderDetails from "../OrderDetailsDrawer";
import useRoutes from "@/hooks/useRoutes";
import { setContainer } from "@/store/uiSlice";
import { useAppDispatch, useAppSelector } from "@/hooks";

import useResumeOrder from "../../hooks/useResumeOrder";
import PerformanceIndicator from "./components/PerformanceIndicator";
import { useOrdersPageContext } from "../../OrdersPageContext";
import { useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { MoreVerticalIcon, ReloadIcon } from "@hugeicons/core-free-icons";
import _ from "lodash";
import { Branch } from "@/types/branch";

const PAGE_SIZE = 10;

const OrdersGrid = () => {
    const { organizationId, orderId } = useParams<{ organizationId: string; orderId: string }>();
    const navigate = useNavigate();
    const [_, getUrl] = useRoutes();
    const dispatch = useAppDispatch();
    const [fetchOrders, { data, isLoading }] = useLazyFetchOrganizationOrdersQuery();
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        page: 0,
        pageSize: PAGE_SIZE,
    });
    const [pageCursors, setPageCursors] = useState<{ [page: number]: any }>({ 0: null });
    const { name, lastname, orderNumber } = useOrdersPageContext();
    const activeBranch = useAppSelector((state) => state.ui?.activeBranch);
    const branches = useAppSelector((state) => state.organizations[organizationId]?.branches);

    const localOrder = useMemo(() => {
        if (!orderId) return null;
        return data?.orders?.find((o) => o.id === orderId) || null;
    }, [data?.orders, orderId]);

    const { data: fetchedOrder } = useGetOrderByIdQuery(
        { organizationId: organizationId || "", orderId: orderId || "" },
        { skip: !organizationId || !orderId || Boolean(localOrder) }
    );

    const activeOrder = useMemo(() => {
        if (!orderId) return null;
        if (localOrder) return localOrder;
        if (fetchedOrder && fetchedOrder.id === orderId) return fetchedOrder as Order;
        return null;
    }, [orderId, localOrder, fetchedOrder]);

    const handleFetch = useCallback((action: "none" | "next" | "previous", currentData?: any, filters?: { name?: string; lastname?: string; orderNumber?: string; branchId?: string }, customCursor?: any) => {
        if (!organizationId) return;

        fetchOrders({
            organizationId,
            pageSize: PAGE_SIZE,
            action,
            firstDocument: currentData?.firstDocument,
            lastDocument: customCursor ? customCursor : currentData?.lastDocument,
            name: filters?.name || undefined,
            lastname: filters?.lastname || undefined,
            orderNumber: filters?.orderNumber || undefined,
            branchId: filters?.branchId || undefined,
        } as any);
    }, [organizationId, fetchOrders]);

    const isInitialMount = useRef(true);

    useEffect(() => {
        const sanitizedName = sanitizeForSearch(name);
        const sanitizedLastname = sanitizeForSearch(lastname);
        const filters = {
            name: sanitizedName,
            lastname: sanitizedLastname,
            orderNumber: orderNumber.trim(),
            branchId: activeBranch || undefined
        };

        if (isInitialMount.current) {
            setPageCursors({ 0: null });
            handleFetch("none", undefined, filters);
            isInitialMount.current = false;
            return;
        }

        const handler = setTimeout(() => {
            setPageCursors({ 0: null });
            handleFetch("none", undefined, filters);
            setPaginationModel(prev => ({ ...prev, page: 0 }));
        }, 500);

        return () => clearTimeout(handler);
    }, [name, lastname, orderNumber, activeBranch, handleFetch]);

    useEffect(() => {
        if (data?.lastDocument) {
            setPageCursors(prev => ({
                ...prev,
                [paginationModel.page + 1]: data.lastDocument
            }));
        }
    }, [data?.lastDocument, paginationModel.page]);

    const handlePaginationModelChange = (newModel: GridPaginationModel) => {
        const sanitizedName = sanitizeForSearch(name);
        const sanitizedLastname = sanitizeForSearch(lastname);
        const filters = {
            name: sanitizedName,
            lastname: sanitizedLastname,
            orderNumber: orderNumber.trim(),
            branchId: activeBranch || undefined
        };

        let action: "none" | "next" | "previous" = "none";
        let customCursor = undefined;

        if (newModel.page === 0) {
            action = "none";
        } else {
            action = "next";
            customCursor = pageCursors[newModel.page];
        }

        handleFetch(action, undefined, filters, customCursor);
        setPaginationModel(newModel);
    };

    const handleRowClick = useCallback((params: GridRowParams<Order>) => {
        const url = getUrl("organizations.orders.details", { organizationId: organizationId ?? "", orderId: params.row.id });
        navigate(url as string);
    }, [getUrl, navigate, organizationId]);

    const columns: GridColDef[] = useMemo(
        () => [
            {
                flex: 0.5,
                field: "number",
                headerName: "#Orden",
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams<Order>) => (
                    <Stack flex={1} direction="row" alignItems="center" gap={1} justifyContent="space-between">
                        <Typography>
                            {params.row.number ? `#${params.row.number}` : "N/A"}
                        </Typography>
                        <PerformanceIndicator
                            orderedAt={params.row.orderedAt}
                            approvedAt={params.row.approvedAt}
                            sendingAt={params.row.sendingAt}
                            completedAt={params.row.completedAt}
                        />
                    </Stack>
                ),
            },
            {
                flex: 2,
                field: "client",
                headerName: "Cliente",
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams<Order>) => (
                    <Typography>
                        {`${params.row.client?.name ?? ""} ${params.row.client?.lastname ?? ""}`.trim() || "N/A"}
                    </Typography>
                ),
            },
            {
                flex: 1,
                field: "status",
                headerName: "Estatus",
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams<Order>) => (
                    params.row.status === "rejected"
                        ? <Chip variant={params?.row?.rejectedReason === "timeout" ? "outlined" : "filled"} label={params?.row?.rejectedReason === "timeout" ? "Auto rechazada" : "Rechazada"} size="small" color="error" />
                        : <Chip label="Completada" size="small" color="success" />
                ),
            },
            {
                flex: 1,
                field: "branch",
                headerName: "Sucursal",
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams<Order>) => {
                    const branchId = params.row.branchId ? params.row.branchId : params.row.branch?.id;
                    const branchTitle = branches?.find((b: Branch) => b.id === branchId)?.title;
                    return (
                        <Typography>
                            {branchTitle || "N/A"}
                        </Typography>
                    );

                },
            },
            {
                flex: 1,
                field: "source",
                headerName: "Canal",
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams<Order>) => (
                    <Typography>
                        {params.row.source || "N/A"}
                    </Typography>
                ),
            },
            {
                flex: 1,
                field: "orderedAt",
                headerName: "Fecha/Hora",
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams<Order>) => {
                    const timestamp = params.row.orderedAt || params.row.createdAt || params.row.completedAt;
                    return (
                        <Typography>
                            {timestamp ? format(new Date(timestamp), "dd MMM, h:mm a") : "N/A"}
                        </Typography>
                    );
                },
            },
            {
                flex: 0.5,
                field: "actions",
                headerName: " ",
                sortable: false,
                disableColumnMenu: true,
                align: "right",
                headerAlign: "right",
                renderCell: (params: GridRenderCellParams<Order>) => {
                    return <ActionMenu row={params.row} />;
                },
            },
        ],
        [organizationId, branches]
    );

    useEffect(() => {
        dispatch(setContainer("fluid"));
        return () => {
            dispatch(setContainer("fixed"));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Stack spacing={2}>
            <Paper>
                <DataGrid
                    autoHeight
                    rows={data?.orders || []}
                    columns={columns}
                    rowCount={data?.totalDocuments || 0}
                    loading={isLoading}
                    pageSizeOptions={[PAGE_SIZE]}
                    paginationModel={paginationModel}
                    paginationMode="server"
                    onPaginationModelChange={handlePaginationModelChange}
                    onRowClick={handleRowClick}
                    disableRowSelectionOnClick
                    getRowId={(row) => row.id}
                    getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd")}
                    density="comfortable"
                />
            </Paper>
            <OrderDetails order={activeOrder} />
        </Stack>
    );
};

const ActionMenu = ({ row }: { row: Order }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const { handleResumeOrder } = useResumeOrder();

    const canResume = row.status === "rejected" && row.rejectedReason === "timeout";

    if (!canResume) {
        return <div style={{ width: 40 }} />;
    }

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (event: React.MouseEvent) => {
        event.stopPropagation();
        setAnchorEl(null);
    };

    const handleAction = async (event: React.MouseEvent) => {
        event.stopPropagation();
        handleClose(event);
        await handleResumeOrder(row);
    };

    return (
        <>
            <IconButton onClick={handleClick} size="small">
                <HugeiconsIcon icon={MoreVerticalIcon} size={20} />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                {canResume && (
                    <MenuItem onClick={handleAction}>
                        <ListItemIcon>
                            <HugeiconsIcon icon={ReloadIcon} size={20} />
                        </ListItemIcon>
                        <ListItemText>Retomar pedido</ListItemText>
                    </MenuItem>
                )}
            </Menu>
        </>
    );
};

export default OrdersGrid;
